/**
 * Utility for cleaning up outdated localStorage data after successful authentication
 */
import { STORAGE_KEYS } from '../constants/identity';

/**
 * List of localStorage keys that should be cleaned after successful registration/login
 * These are typically temporary data used during the purchase/registration flow
 */
const CLEANUP_KEYS = [
  // Purchase flow related data
  'purchase_result',
  // NOTE: purchase_flow_state is NOT cleaned here - users may authenticate mid-purchase
  // It should only be cleaned after successful payment completion
  STORAGE_KEYS.OAUTH_SUBS_USER_ID,
  STORAGE_KEYS.LEGACY_OAUTH_SUBS_USER_ID,

  // Pending order data
  'pendingOrderNumber',
  'pendingPlanName',
  'pendingOrderTimestamp',

  // Temporary country selection (user can re-select)
  'homeCountry',

  // Old auth tokens (auth_token will be set fresh)
  'authToken', // Old token format
  'refresh_token', // If using refresh tokens

  // Debug/development data
  STORAGE_KEYS.USER_ID // Generic user_id that might conflict
];

/**
 * Keys to preserve during cleanup
 * These are important for user experience and shouldn't be removed
 */
const PRESERVE_KEYS = [
  STORAGE_KEYS.AUTH_TOKEN, // Current valid token
  'admin_session_info', // Admin impersonation session - must be preserved for "Return to Admin" feature
  'theme', // User's dark/light mode preference - must persist across OAuth redirects
  'showIpadScreenshot', // UI preference
  'showIpadV1Screenshot' // UI preference
];

/**
 * Clean up outdated localStorage and sessionStorage data after successful authentication
 * This prevents conflicts and ensures fresh state for authenticated users
 *
 * @param {Object} options - Cleanup options
 * @param {boolean} options.preservePreferences - Whether to preserve UI preferences (default: true)
 * @param {Array<string>} options.additionalCleanup - Additional keys to clean up
 * @param {Array<string>} options.additionalPreserve - Additional keys to preserve
 * @param {boolean} options.cleanSessionStorage - Whether to also clean sessionStorage (default: true)
 */
export const cleanupLocalStorageAfterAuth = (options = {}) => {
  const {
    preservePreferences = true,
    additionalCleanup = [],
    additionalPreserve = [],
    cleanSessionStorage = true
  } = options;

  console.log('🧹 Cleaning up localStorage and sessionStorage after successful authentication...');
  
  // Get current localStorage content for logging
  const beforeCleanup = Object.keys(localStorage).reduce((acc, key) => {
    acc[key] = localStorage.getItem(key);
    return acc;
  }, {});
  
  console.log('📦 localStorage before cleanup:', Object.keys(beforeCleanup));
  
  // Determine keys to preserve
  let keysToPreserve = [...PRESERVE_KEYS, ...additionalPreserve];
  
  // If preserving preferences, keep UI-related keys
  if (preservePreferences) {
    keysToPreserve.push('showIpadScreenshot', 'showIpadV1Screenshot');
  }
  
  // Determine keys to clean
  const keysToClean = [...CLEANUP_KEYS, ...additionalCleanup];
  
  // Perform cleanup
  let cleanedCount = 0;
  keysToClean.forEach(key => {
    if (localStorage.getItem(key) !== null && !keysToPreserve.includes(key)) {
      try {
        localStorage.removeItem(key);
        cleanedCount++;
        console.log(`🗑️  Removed: ${key}`);
      } catch (error) {
        console.warn(`⚠️  Failed to remove ${key}:`, error);
      }
    }
  });
  
  // Clean sessionStorage if requested
  let sessionCleanedCount = 0;
  if (cleanSessionStorage) {
    console.log('🧹 Cleaning up sessionStorage...');
    keysToClean.forEach(key => {
      if (sessionStorage.getItem(key) !== null && !keysToPreserve.includes(key)) {
        try {
          sessionStorage.removeItem(key);
          sessionCleanedCount++;
          console.log(`🗑️  Removed from sessionStorage: ${key}`);
        } catch (error) {
          console.warn(`⚠️  Failed to remove ${key} from sessionStorage:`, error);
        }
      }
    });
  }

  // Log what was preserved
  const preservedKeys = Object.keys(localStorage);
  const preservedSessionKeys = Object.keys(sessionStorage);
  console.log(`✅ Cleanup complete! localStorage: Removed ${cleanedCount} items, preserved ${preservedKeys.length} items`);
  if (cleanSessionStorage) {
    console.log(`✅ sessionStorage: Removed ${sessionCleanedCount} items, preserved ${preservedSessionKeys.length} items`);
  }

  return {
    cleanedCount,
    sessionCleanedCount,
    preservedKeys,
    preservedSessionKeys,
    beforeCleanup: Object.keys(beforeCleanup),
    afterCleanup: preservedKeys
  };
};

/**
 * Clean up specific purchase flow data from both localStorage and sessionStorage
 * Use this for targeted cleanup during purchase completion
 *
 * @param {boolean} preserveForRefresh - If true, keeps data in sessionStorage for page refreshes (default: false)
 */
export const cleanupPurchaseFlowData = (preserveForRefresh = false) => {
  console.log('🛒 Cleaning up purchase flow data...');

  const purchaseKeys = [
    'purchase_result',
    'purchase_flow_state',
    STORAGE_KEYS.OAUTH_SUBS_USER_ID,
    STORAGE_KEYS.LEGACY_OAUTH_SUBS_USER_ID,
    'pendingOrderNumber',
    'pendingPlanName',
    'pendingOrderTimestamp'
  ];

  let cleanedCount = 0;
  let sessionCleanedCount = 0;

  // Clean localStorage
  purchaseKeys.forEach(key => {
    if (localStorage.getItem(key) !== null) {
      localStorage.removeItem(key);
      cleanedCount++;
      console.log(`🗑️  Removed from localStorage: ${key}`);
    }
  });

  // Clean sessionStorage unless preserving for refresh
  if (!preserveForRefresh) {
    purchaseKeys.forEach(key => {
      if (sessionStorage.getItem(key) !== null) {
        sessionStorage.removeItem(key);
        sessionCleanedCount++;
        console.log(`🗑️  Removed from sessionStorage: ${key}`);
      }
    });
  }

  console.log(`✅ Purchase flow cleanup complete! localStorage: ${cleanedCount} items, sessionStorage: ${sessionCleanedCount} items.`);
  return {
    cleanedCount,
    sessionCleanedCount,
    preservedForRefresh: preserveForRefresh
  };
};

/**
 * Emergency cleanup - removes all localStorage data except critical auth
 * Use with caution - this will reset all user preferences
 */
export const emergencyCleanup = () => {
  console.warn('🚨 Emergency localStorage cleanup initiated...');
  
  // Save critical data
  const authToken = localStorage.getItem('auth_token');
  
  // Clear everything
  localStorage.clear();
  
  // Restore critical data
  if (authToken) {
    localStorage.setItem('auth_token', authToken);
  }
  
  console.log('🚨 Emergency cleanup complete. Only auth_token preserved.');
  return { success: true };
};

/**
 * Utility to check for potentially stale data
 * Returns information about data that might need cleanup
 */
export const analyzeLocalStorageHealth = () => {
  const analysis = {
    potentiallyStale: [],
    preserved: [],
    unknown: [],
    total: 0
  };
  
  Object.keys(localStorage).forEach(key => {
    analysis.total++;
    
    if (CLEANUP_KEYS.includes(key)) {
      analysis.potentiallyStale.push(key);
    } else if (PRESERVE_KEYS.includes(key)) {
      analysis.preserved.push(key);
    } else {
      analysis.unknown.push(key);
    }
  });
  
  console.log('📊 localStorage Health Analysis:', analysis);
  return analysis;
};

export default {
  cleanupLocalStorageAfterAuth,
  cleanupPurchaseFlowData,
  emergencyCleanup,
  analyzeLocalStorageHealth
};
